/**
 * BookingExpensesCard
 *
 * Unified booking expenses + a single line-selectable payment flow.
 *
 * The folio is amount-based (paid via its balance), so it shows as ONE
 * selectable "Folio" line (= folio balance) with its charges as a read-only
 * detail grouped by who incurred them. Per-guest charges have real per-line
 * statuses, so they show as individually selectable lines grouped by guest.
 * Selecting + paying hits POST /bookings/{id}/settle/ as one atomic action.
 */
import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { useStatementLink } from '@/shared/hooks/useStatementLink';
import { notify } from '@/shared/services';
import {
  DollarSign, CreditCard, ChevronDown, ChevronUp, FileText, Plus, Loader2, CheckCircle2, Receipt, ExternalLink,
  Link2, Unlink,
} from 'lucide-react';
import { format } from 'date-fns';
import { useTranslation } from '@/core';
import { useLocationCurrency } from '@/shared/hooks/useLocationCurrency';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/theme/components/ui/card';
import { Button } from '@/theme/components/ui/button';
import { Checkbox } from '@/theme/components/ui/checkbox';
import { Separator } from '@/theme/components/ui/separator';
import { Skeleton } from '@/theme/components/ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/theme/components/ui/collapsible';
import { ScrollArea } from '@/theme/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { ChargeSellableDialog } from '@/modules/guests/components/ChargeSellableDialog';
import { ErrorState } from '@/shared/components/ErrorState';
import { ConfirmDialog } from '@/shared/components/ConfirmDialog';
import { useSyncConfig } from '@/modules/integrations/bookinglayer/hooks/useBookinglayer';
import { useAuth } from '@/core/auth/useAuth';
import { canManageCompanyIntegrations } from '@/core/auth/modulePermissions';
import { useFolio } from '../../hooks/useFolio';
import {
  useBookingStatement,
  useSetFolioChargeBlLink,
  useSettleBookingCharges,
  useSettleBookingSplit,
} from '../../hooks/useBookingSettlement';
import { BookingPaymentDialog, type BookingPaymentSubmit } from './BookingPaymentDialog';
import { BookingPaymentSplitDialog, type SplitGuest } from './BookingPaymentSplitDialog';
import type { ExpenseStatementChargeLine } from '../../types';
import { integrationSourceOf } from '@/shared/utils';

const FOLIO_LINE = '__folio__';
/** Bucket key for folio charges with no `incurredBy` (BL room/package lines,
 *  manual booking-level charges). Always rendered last. */
const FOLIO_BOOKING_LEVEL = '__booking_level__';

const SOURCE_LABEL_KEY: Record<string, string> = {
  folio_charge: 'folio.category.other',
  room: 'folio.category.room',
  pos: 'folio.category.pos',
  posorder: 'guest.chargeSource.posorder',
  activity: 'folio.category.activity',
  activitybooking: 'guest.chargeSource.activitybooking',
  servicebooking: 'guest.chargeSource.servicebooking',
  service: 'folio.category.service',
  penalty: 'folio.category.penalty',
  product: 'person.guest.chargeSource.product',
  shopproduct: 'person.guest.chargeSource.shopProduct',
  guest_charge: 'guest.chargeSource.unknown',
};

interface BookingExpensesCardProps {
  bookingId: string;
  /**
   * Outstanding balance mirrored from BookingLayer (`integration_due_amount`).
   * Read-only here: BL debt is settled on the BL side and synced back, not
   * charged from Shaka. Shown as an info line so the card's "Booking debt"
   * total reflects the full debt instead of just the local statement.
   */
  integrationDue?: number;
  /** Currency for the BL outstanding amount (falls back to location currency). */
  integrationCurrency?: string;
  /** Booking source — when `'bookinglayer'` (+ `bookingExternalId`) the payment
   *  dialog warns if BL outbound sync isn't actively pushing the payment. */
  bookingSource?: string;
  /** BL order id, used to deep-link the booking in BookingLayer. */
  bookingExternalId?: string;
  /**
   * Where to open this booking in the system that owns it, supplied by the
   * backend. The deck used to build it from a hardcoded vendor base URL.
   */
  bookingExternalUrl?: string;
  /** Booking guests — payers for the booking-level split payment dialog. */
  guests?: SplitGuest[];
}

/** Imperative handle so the booking detail's "Add payment" quick action can
 *  drive the in-card payment flow (select open lines + open the dialog). */
export interface BookingExpensesCardHandle {
  /** Select every open local line and open the payment dialog. No-op with a
   *  notice when there's nothing chargeable from Shaka. */
  startPayment: () => void;
}

export const BookingExpensesCard = forwardRef<BookingExpensesCardHandle, BookingExpensesCardProps>(
  function BookingExpensesCard({ bookingId, integrationDue, integrationCurrency, bookingSource, bookingExternalId, bookingExternalUrl, guests = [] }, ref) {
  const { t } = useTranslation();
  const { openBookingStatement } = useStatementLink();
  const { formatPrice } = useLocationCurrency();

  // BL-linked booking → check whether outbound is actively pushing payments
  // live. If not, the payment dialog shows a "register it in BL" notice. Only
  // fetch the sync config for BL bookings (no connection on plain locations).
  // Vendor-specific on purpose: it decides whether to fetch BookingLayer's
  // sync config. Asking BL about another integration's booking would 404.
  const isBLBooking = integrationSourceOf({ source: bookingSource }) === 'bookinglayer' && !!bookingExternalId;
  // sync-config requires company.manage_integrations; a Frontdesk operator
  // would 403 in the background. Only fetch when the user can manage integrations.
  const { user } = useAuth();
  const { data: syncConfig } = useSyncConfig({
    enabled: isBLBooking && canManageCompanyIntegrations(user),
  });
  const outboundActive =
    !!syncConfig && syncConfig.outboundPayments && syncConfig.outboundExecutionMode === 'live';
  // Only treat outbound as "off" once we actually know (config loaded) — avoids
  // flashing a false "won't sync" warning while the config is still loading.
  const outboundKnownOff = isBLBooking && !!syncConfig && !outboundActive;
  const blUrl = isBLBooking ? (bookingExternalUrl || null) : null;

  const { data: folio } = useFolio(bookingId);
  const { data: statement, isLoading, error: statementError, refetch: refetchStatement } = useBookingStatement(bookingId);
  const settle = useSettleBookingCharges(bookingId);
  const splitSettle = useSettleBookingSplit(bookingId);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [paymentsOpen, setPaymentsOpen] = useState(false);
  const [folioDetailOpen, setFolioDetailOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [splitOpen, setSplitOpen] = useState(false);
  const [addChargeOpen, setAddChargeOpen] = useState(false);
  // Charge pending a BL link/unlink confirmation (client has line-level
  // control over which on-site charges BL invoices reflect).
  const [blLinkTarget, setBlLinkTarget] = useState<ExpenseStatementChargeLine | null>(null);
  const blLink = useSetFolioChargeBlLink(bookingId);
  // Categories the sync config excludes from the BL charge push (e.g. 'room',
  // which already exists in BL) — no toggle for those, it would be a no-op.
  const blExcludedCategories = syncConfig?.outboundChargeCategoriesExcluded ?? ['room'];
  const canToggleBlLink = (line: ExpenseStatementChargeLine) =>
    isBLBooking
    && line.origin === 'folio'
    && !blExcludedCategories.includes(line.category ?? '');

  const folioBalance = parseFloat(folio?.balance ?? '0') || 0;
  const folioSelectable = folioBalance > 0;
  const balanceDue = parseFloat(statement?.totals.balanceDue ?? '0') || 0;
  // BL outstanding (read-only mirror). The card's "Booking debt" must reflect
  // the full debt = local statement + BL due, so a BL booking with no local
  // charges no longer shows 0 next to a non-zero BL balance.
  const blDue = integrationDue && integrationDue > 0 ? integrationDue : 0;
  const totalDebt = balanceDue + blDue;

  const openCharges = useMemo(
    () => (statement?.charges ?? []).filter((c) => c.status === 'open'),
    [statement?.charges],
  );
  const folioCharges = useMemo(() => openCharges.filter((c) => c.origin === 'folio'), [openCharges]);

  // Folio detail grouped by whoever incurred each charge. The folio itself stays
  // a single selectable line (it settles by amount, never per line), so this is
  // attribution only: a POS room charge shows under its guest instead of being
  // flattened into the booking total. Charges with no `incurredBy` (BL room /
  // package lines, manual booking-level charges) fall into a last bucket.
  const folioChargeGroups = useMemo(() => {
    const byGuest = new Map<string, { name: string; lines: ExpenseStatementChargeLine[]; total: number }>();
    for (const c of folioCharges) {
      const key = c.belongsToGuestId ?? FOLIO_BOOKING_LEVEL;
      const g = byGuest.get(key) ?? {
        name: c.belongsToGuestName ?? t('expenseStatement.bookingLevel', 'Booking-level'),
        lines: [],
        total: 0,
      };
      g.lines.push(c);
      g.total += parseFloat(c.amount) || 0;
      byGuest.set(key, g);
    }
    return Array.from(byGuest.entries())
      .map(([key, value]) => ({ key, ...value }))
      .sort((a, b) => {
        if (a.key === FOLIO_BOOKING_LEVEL) return 1;
        if (b.key === FOLIO_BOOKING_LEVEL) return -1;
        return b.total - a.total;
      });
  }, [folioCharges, t]);

  // Guest charges (per-line settle) grouped by owner.
  const guestGroups = useMemo(() => {
    const byGuest = new Map<string, { name: string; lines: ExpenseStatementChargeLine[] }>();
    for (const c of openCharges) {
      if (c.origin !== 'guest') continue;
      const key = c.belongsToGuestId ?? '—';
      const g = byGuest.get(key) ?? { name: c.belongsToGuestName ?? t('expenseStatement.guest', 'Guest'), lines: [] };
      g.lines.push(c);
      byGuest.set(key, g);
    }
    return Array.from(byGuest.values());
  }, [openCharges, t]);

  const guestCharges = useMemo(() => openCharges.filter((c) => c.origin === 'guest'), [openCharges]);
  const selectableIds = useMemo(
    () => [...(folioSelectable ? [FOLIO_LINE] : []), ...guestCharges.map((c) => c.id)],
    [folioSelectable, guestCharges],
  );
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));

  // Total of everything selectable from Shaka (folio balance + open guest
  // charges). Used to decide whether opening the payment dialog can actually
  // collect anything — a set of €0 charges is selectable but not payable.
  const selectableTotal =
    (folioSelectable ? folioBalance : 0)
    + guestCharges.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);

  const folioSelected = selected.has(FOLIO_LINE);
  const selectedGuestLines = guestCharges
    .filter((c) => selected.has(c.id))
    .map((c) => ({ id: c.id, description: c.description, amount: parseFloat(c.amount) || 0 }));
  const selectedLines = [
    ...(folioSelected ? [{ id: FOLIO_LINE, description: t('bookingExpenses.folioLine', 'Booking folio'), amount: folioBalance }] : []),
    ...selectedGuestLines,
  ];
  const selectedTotal = selectedLines.reduce((s, l) => s + l.amount, 0);

  // BookingLayer notice for the payment dialog:
  //  - no local lines (BL-only debt): always surface the booking link — as a
  //    'warning' when we know outbound is off, otherwise neutral 'info'.
  //  - paying local lines: only warn when outbound is known off (the payment
  //    won't reach BL automatically).
  const blNotice: { url: string; tone: 'warning' | 'info' } | null = !blUrl
    ? null
    : selectedLines.length === 0
      ? { url: blUrl, tone: outboundKnownOff ? 'warning' : 'info' }
      : outboundKnownOff
        ? { url: blUrl, tone: 'warning' }
        : null;

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectableIds));

  // Driven by the "Add payment" quick action in the booking detail sidebar:
  // pre-select every open local line and open the payment dialog. If there's
  // nothing chargeable from Shaka (settled, or BL-only due which is read-only),
  // give explicit feedback instead of silently opening an empty dialog.
  const startPayment = () => {
    // Booking-level "Add payment" collects against the shared folio balance
    // and can split it between payers.
    if (folioBalance > 0) {
      setSplitOpen(true);
      return;
    }
    // BL-only debt: nothing chargeable from Shaka, but still raise the dialog so
    // the operator gets the BL notice + a link to the booking in BookingLayer.
    if (isBLBooking && blDue > 0) {
      setSelected(new Set());
      setPayOpen(true);
      return;
    }
    // Only guest-owned charges left → those are settled from each guest's
    // charge surface, not the booking-level payment.
    if (guestCharges.length > 0) {
      notify.info(t('bookingExpenses.payFromGuest', 'Open charges belong to guests — settle them from each guest.'));
      return;
    }
    notify.info(t('bookingExpenses.nothingToPay', 'No open charges to collect from Shaka.'));
  };
  useImperativeHandle(ref, () => ({ startPayment }));

  const handlePay = (input: BookingPaymentSubmit) => {
    settle.mutate(
      {
        folioAmount: folioSelected ? folioBalance : undefined,
        guestChargeIds: selectedGuestLines.map((l) => l.id),
        method: input.method,
        paidByName: input.paidByName,
        paidByNotes: input.paidByNotes,
      },
      { onSuccess: () => { setPayOpen(false); setSelected(new Set()); } },
    );
  };

  const sourceLabel = (line: ExpenseStatementChargeLine) =>
    t(SOURCE_LABEL_KEY[line.sourceType] ?? 'folio.category.other');

  if (isLoading) {
    return (
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><DollarSign className="h-5 w-5" />{t('booking.chargesAndPayments', 'Charges & Payments')}</CardTitle></CardHeader>
        <CardContent className="space-y-3"><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-3/4" /><Skeleton className="h-4 w-1/2" /></CardContent>
      </Card>
    );
  }

  // A failed statement fetch must not render as "fully settled" — the
  // missing data would zero out balanceDue and hide real open charges.
  if (statementError) {
    return (
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><DollarSign className="h-5 w-5" />{t('booking.chargesAndPayments', 'Charges & Payments')}</CardTitle></CardHeader>
        <CardContent>
          <ErrorState
            compact
            className="py-6"
            message={t('bookingExpenses.loadError', 'Could not load charges and payments.')}
            onRetry={() => refetchStatement()}
          />
        </CardContent>
      </Card>
    );
  }

  // Local lines the operator can select & charge from Shaka.
  const hasLocalOpen = folioSelectable || guestCharges.length > 0;
  // "Fully settled" only when there's nothing local AND no BL debt mirrored.
  const fullySettled = !hasLocalOpen && blDue === 0;

  return (
    <>
      <Card className="flex h-full flex-col">
        <CardHeader>
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              {t('booking.chargesAndPayments', 'Charges & Payments')}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {t('bookingExpenses.bookingDebt', 'Booking debt')}:{' '}
              <span className={cn('font-mono font-semibold tabular-nums', totalDebt > 0 ? 'text-warning' : 'text-success')}>
                {formatPrice(totalDebt)}
              </span>
            </p>
          </div>
        </CardHeader>

        <ScrollArea className="flex-1 max-h-[440px]">
          <CardContent className="space-y-4">
            {fullySettled ? (
              <div className="flex items-center gap-2 py-6 text-sm text-success">
                <CheckCircle2 className="h-4 w-4" />{t('bookingExpenses.allSettled', 'No open charges — fully settled.')}
              </div>
            ) : (
              <>
                {/* Booking folio — one amount-based selectable line + read-only detail */}
                {folioSelectable && (
                  <div className="space-y-1">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                      {t('bookingExpenses.bookingGroup', 'Booking')}
                    </p>
                    <label className="flex items-center gap-2 rounded px-1 py-1 text-sm hover:bg-muted/50 cursor-pointer">
                      <Checkbox checked={folioSelected} onCheckedChange={() => toggle(FOLIO_LINE)} />
                      <span className="flex-1 text-foreground">{t('bookingExpenses.folioLine', 'Booking folio')}</span>
                      <span className="font-mono font-semibold tabular-nums shrink-0">{formatPrice(folioBalance)}</span>
                    </label>
                    {folioCharges.length > 0 && (
                      <Collapsible open={folioDetailOpen} onOpenChange={setFolioDetailOpen}>
                        <CollapsibleTrigger asChild>
                          <button className="flex items-center gap-1 pl-7 text-[11px] text-muted-foreground hover:text-foreground">
                            <Receipt className="h-3 w-3" />
                            {t('bookingExpenses.folioCharges', '{{count}} charge(s)', { count: folioCharges.length })}
                            {folioDetailOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                          </button>
                        </CollapsibleTrigger>
                        {/* Read-only detail, grouped by who incurred each charge.
                            No checkboxes here on purpose: the folio is paid once,
                            by amount, via the selectable line above. */}
                        <CollapsibleContent className="mt-1 space-y-2 pl-7">
                          {folioChargeGroups.map((group) => (
                            <div key={group.key} className="space-y-0.5">
                              <div className="flex items-center justify-between gap-2 text-[11px]">
                                <span className="truncate font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                                  {group.name}
                                </span>
                                <span className="shrink-0 font-mono tabular-nums text-muted-foreground">
                                  {formatPrice(group.total)}
                                </span>
                              </div>
                              {group.lines.map((c) => (
                                <div key={c.id} className="flex items-center justify-between gap-2 text-[11px]">
                                  <span className="flex flex-col min-w-0">
                                    <span className="truncate text-foreground/80">{c.description}</span>
                                    <span className="text-muted-foreground">
                                      {sourceLabel(c)}
                                      {canToggleBlLink(c) && c.blExcluded && (
                                        <span className="ml-1.5 text-warning">
                                          · {t('bookingExpenses.blNotLinked', 'not in BL')}
                                        </span>
                                      )}
                                    </span>
                                  </span>
                                  <span className="flex items-center gap-1 shrink-0">
                                    <span className="font-mono tabular-nums pl-2 text-muted-foreground">{formatPrice(parseFloat(c.amount) || 0)}</span>
                                    {canToggleBlLink(c) && (
                                      <button
                                        className="rounded p-0.5 text-muted-foreground hover:text-foreground hover:bg-muted"
                                        onClick={() => setBlLinkTarget(c)}
                                        disabled={blLink.isPending}
                                        title={c.blExcluded
                                          ? t('bookingExpenses.blLinkAction', 'Link to Bookinglayer')
                                          : t('bookingExpenses.blUnlinkAction', 'Unlink from Bookinglayer')}
                                      >
                                        {c.blExcluded ? <Link2 className="h-3 w-3" /> : <Unlink className="h-3 w-3" />}
                                      </button>
                                    )}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ))}
                        </CollapsibleContent>
                      </Collapsible>
                    )}
                  </div>
                )}

                {/* Per-guest charges — per-line selectable */}
                {guestGroups.map((g, i) => (
                  <div key={i} className="space-y-0.5">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">{g.name}</p>
                    {g.lines.map((line) => (
                      <label key={line.id} className="flex items-center gap-2 rounded px-1 py-1 text-xs hover:bg-muted/50 cursor-pointer">
                        <Checkbox checked={selected.has(line.id)} onCheckedChange={() => toggle(line.id)} />
                        <span className="flex flex-1 flex-col min-w-0">
                          <span className="truncate text-foreground">{line.description}</span>
                          <span className="text-muted-foreground">{sourceLabel(line)}</span>
                        </span>
                        <span className="font-mono font-medium tabular-nums shrink-0">{formatPrice(parseFloat(line.amount) || 0)}</span>
                      </label>
                    ))}
                  </div>
                ))}

                {/* BookingLayer outstanding — read-only mirror, settled on BL side */}
                {blDue > 0 && (
                  <div className="space-y-1">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                      {t('bookingExpenses.bookingLayerGroup', 'BookingLayer')}
                    </p>
                    <div className="flex items-start gap-2 rounded px-1 py-1 text-sm">
                      <ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className="flex flex-1 flex-col min-w-0">
                        <span className="text-foreground">{t('bookingExpenses.blDueLine', 'Outstanding balance')}</span>
                        <span className="text-[11px] text-muted-foreground">
                          {t('bookingExpenses.blDueHint', 'Settled in BookingLayer and mirrored here — not charged from Shaka.')}
                        </span>
                      </span>
                      <span className="font-mono font-semibold tabular-nums shrink-0 text-warning">
                        {formatPrice(blDue, { currencyCode: integrationCurrency })}
                      </span>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Payments history */}
            {(statement?.payments?.length ?? 0) > 0 && (
              <>
                <Separator />
                <Collapsible open={paymentsOpen} onOpenChange={setPaymentsOpen}>
                  <CollapsibleTrigger asChild>
                    <button className="flex w-full items-center justify-between text-sm text-muted-foreground hover:text-foreground transition-colors">
                      <span className="flex items-center gap-1.5">
                        <CreditCard className="h-3.5 w-3.5" />
                        {t('bookingExpenses.paymentsRecorded', 'Payments recorded')} ({statement?.payments.length})
                      </span>
                      <span className="flex items-center gap-2">
                        <span className="font-mono text-[13px] font-semibold tabular-nums text-success">
                          {formatPrice(parseFloat(statement?.totals.paymentsTotal ?? '0') || 0)}
                        </span>
                        {paymentsOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                      </span>
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-2 space-y-2 border-l-2 border-muted pl-4 ml-1">
                    {(statement?.payments ?? []).map((p) => (
                      <div key={p.id} className="flex justify-between text-xs">
                        <span className="flex flex-col">
                          <span className="text-muted-foreground">{format(new Date(p.date), 'dd MMM yyyy')}</span>
                          <span className="text-foreground">{t(`folio.method.${p.method}`)}{p.paidByLabel ? ` · ${p.paidByLabel}` : ''}</span>
                        </span>
                        <span className="font-mono font-medium tabular-nums shrink-0 text-success">{formatPrice(parseFloat(p.amount) || 0)}</span>
                      </div>
                    ))}
                  </CollapsibleContent>
                </Collapsible>
              </>
            )}
          </CardContent>
        </ScrollArea>

        {hasLocalOpen && (
          <CardFooter className="flex-col gap-2 border-t pt-4">
            <div className="flex w-full items-center justify-between text-sm">
              <button onClick={toggleAll} className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline">
                {allSelected ? t('bookingExpenses.clearSelection', 'Clear') : t('bookingExpenses.selectAll', 'Select all')}
              </button>
              <span className="text-muted-foreground">
                {t('bookingPayment.linesSelected', '{{count}} line(s) selected', { count: selectedLines.length })}
                {' · '}
                <span className="font-mono font-semibold tabular-nums text-foreground">{formatPrice(selectedTotal)}</span>
              </span>
            </div>
            <Button className="w-full" disabled={selectedTotal <= 0 || settle.isPending} onClick={() => setPayOpen(true)}>
              {settle.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
              {t('bookingExpenses.chargeSelected', 'Charge selected')} {selectedTotal > 0 ? formatPrice(selectedTotal) : ''}
            </Button>
          </CardFooter>
        )}

        {/* Secondary actions — always available at the foot of the card. */}
        <CardFooter className="gap-2 border-t bg-muted/30 py-3">
          <Button variant="outline" size="sm" className="flex-1" onClick={() => setAddChargeOpen(true)}>
            <Plus className="mr-1 h-4 w-4" />{t('guestCharges.addCharge', 'Add charge')}
          </Button>
          <Button variant="outline" size="sm" className="flex-1" onClick={() => openBookingStatement(bookingId)}>
            <FileText className="mr-1 h-4 w-4" />{t('expenseStatement.open')}
          </Button>
        </CardFooter>
      </Card>

      <BookingPaymentDialog
        open={payOpen}
        onOpenChange={setPayOpen}
        selectedLines={selectedLines}
        total={selectedTotal}
        onSubmit={handlePay}
        isLoading={settle.isPending}
        blNotice={blNotice}
      />

      <BookingPaymentSplitDialog
        open={splitOpen}
        onOpenChange={setSplitOpen}
        balanceDue={folioBalance}
        guests={guests}
        isLoading={splitSettle.isPending}
        onSubmit={(input) =>
          splitSettle.mutate(input, { onSuccess: () => { setSplitOpen(false); setSelected(new Set()); } })
        }
      />

      <ChargeSellableDialog open={addChargeOpen} bookingId={bookingId} onClose={() => setAddChargeOpen(false)} />

      {/* BL link/unlink confirmation — changes what the booking's BL invoice
          reflects, so it always confirms with the specific charge named. */}
      <ConfirmDialog
        open={blLinkTarget !== null}
        onOpenChange={(open) => { if (!open) setBlLinkTarget(null); }}
        title={blLinkTarget?.blExcluded
          ? t('bookingExpenses.blLinkTitle', 'Link charge to Bookinglayer?')
          : t('bookingExpenses.blUnlinkTitle', 'Unlink charge from Bookinglayer?')}
        description={blLinkTarget?.blExcluded
          ? t(
              'bookingExpenses.blLinkDesc',
              '"{{description}}" ({{amount}}) will be added back to the booking\'s charges in Bookinglayer.',
              {
                description: blLinkTarget?.description ?? '',
                amount: formatPrice(parseFloat(blLinkTarget?.amount ?? '0') || 0),
              },
            )
          : t(
              'bookingExpenses.blUnlinkDesc',
              '"{{description}}" ({{amount}}) will be removed from the booking\'s charges in Bookinglayer. The charge stays in Shaka and the guest still owes it here.',
              {
                description: blLinkTarget?.description ?? '',
                amount: formatPrice(parseFloat(blLinkTarget?.amount ?? '0') || 0),
              },
            )}
        confirmLabel={blLinkTarget?.blExcluded
          ? t('bookingExpenses.blLinkConfirm', 'Link to Bookinglayer')
          : t('bookingExpenses.blUnlinkConfirm', 'Unlink from Bookinglayer')}
        onConfirm={() => {
          if (!blLinkTarget) return;
          blLink.mutate(
            { chargeId: blLinkTarget.id, linked: !!blLinkTarget.blExcluded },
            { onSettled: () => setBlLinkTarget(null) },
          );
        }}
        isLoading={blLink.isPending}
      />
    </>
  );
});

BookingExpensesCard.displayName = 'BookingExpensesCard';
